/**
 * The record, read as the batch record it will become.
 *
 * Shaped like the BMR the plant already works from: a page header on every section
 * (company · BMR – section · Batch No. · product · BMR No. · page), a front matter — product
 * information, the bill of materials, an index of sections, abbreviations, general
 * instructions — then each operation as a section whose table reads
 *
 *     Sr. No. | Details | Limit | Observation | Done By (Sign & Date) | Verified By (Sign & Date)
 *
 * with the observation column left as the operator will find it — "To record", a blank to
 * fill — and a Prepared / Reviewed / Approved block to close. Everything is the envelope the
 * designer edits; nothing here is typed, derived or editable. Reviewers are read-only.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Empty, Flex, Space, Spin, Typography, message } from "antd";
import { Download, PencilRuler, Printer } from "lucide-react";
import { PageRail, usePageReturn } from "@/components/PageChrome";

import * as api from "./designerApi";
import { useDispatch } from "react-redux";
import { addField } from "@/store/reducers/headerContent";
import StepWhy from "@/components/ai/StepWhy";

const { Text } = Typography;

/** The observation cell as the BMR prints it: the recorded value's blank, or the act's tick. */
function observation(step, facts) {
  // What the step says it records is the prompt in the observation cell — the record's words.
  if (step.records) return <span className="blank" title="What this step records">{step.records} ________</span>;
  if (facts.length) return <span className="blank">________ <em>{facts[0].label.toLowerCase()}</em></span>;
  if (step.step_type === "PARAMETER_RECORD" || step.step_type === "COUNT" || step.step_type === "CALCULATION")
    return <span className="blank">________</span>;
  if (step.step_type === "SIGN") return <span className="blank">Sign & Date</span>;
  return <span className="tick">☐ Done</span>;
}

function StepRows({ nodes, depth = 0, start = { n: 0 }, recipeId = null }) {
  return (nodes || []).map((n) => {
    if (n.kind === "LOOP") {
      return (
        <React.Fragment key={n.code}>
          <tr className="band"><td colSpan={6}>
            <b>Repeat for each {n.payload?.over || "—"}</b>
            {n.payload?.kind === "UNTIL_RULE" && n.payload?.ref ? ` until ${n.payload.ref}` : ""}
            {n.payload?.expected ? ` · ${n.payload.expected} expected` : n.payload?.ref && n.payload?.kind !== "UNTIL_RULE" ? ` · count from ${n.payload.ref} at issue` : ""}
            {" — the steps below are recorded once per iteration"}
          </td></tr>
          <StepRows nodes={n.children} depth={depth + 1} start={start} recipeId={recipeId} />
        </React.Fragment>
      );
    }
    if (n.kind === "LANE" || n.kind === "HEADING" || n.kind === "DECISION") {
      return (
        <React.Fragment key={n.code}>
          <tr className="band"><td colSpan={6}><b>{n.label || n.title || n.kind.toLowerCase()}</b></td></tr>
          <StepRows nodes={n.children} depth={depth + 1} start={start} recipeId={recipeId} />
        </React.Fragment>
      );
    }
    start.n += 1;
    const facts = (n.detail || []).filter((d) => !d.identity);
    const limit = facts.map((d) => `${d.label}: ${d.value}`).join(" · ");
    const second = n.signature && n.signature !== "Done by";
    return (
      <tr key={n.code} className={depth ? "in" : ""}>
        <td className="no">{start.n}</td>
        <td className="det">
          <div className="t">{n.title || <i>untitled</i>}<span className="code">{n.code}</span><StepWhy recipeId={recipeId} code={n.code} /></div>
          {n.instruction ? <div className="i">{n.instruction}</div> : null}
          {n.checklist ? (
            <div className="i">
              <b>{n.checklist}</b>
              {(n.checklist_items || []).length
                ? <ol className="bmr-ticks">{n.checklist_items.map((it, i) => <li key={i}>☐ {i + 1}. {it}</li>)}</ol>
                : <span className="muted"> — no items on this list</span>}
            </div>
          ) : null}
          {(n.attachments || []).filter((a) => a.kind === "IMAGE").map((a) => (
            <figure key={a.id} className="bmr-fig"><img src={a.url} alt={a.caption || a.name} /><figcaption>{a.caption || a.name}</figcaption></figure>
          ))}
          {(n.sops || []).length ? <div className="i muted">Procedure: {(n.sops || []).map((s) => `${s.sop_no} ${s.title}`).join(" · ")}</div> : null}
          {(n.rules || []).length ? <div className="r">{n.rules.map((r) => `${r.rule_type} · ${r.name}`).join(" · ")}</div> : null}
        </td>
        <td className="lim">{limit || <span className="na">—</span>}</td>
        <td className="obs">{observation(n, facts)}</td>
        <td className="sig">{n.role || <span className="miss">role not set</span>}<div className="line" /></td>
        <td className="sig">{second ? (n.signature_policy || <span className="miss">not stated</span>) : <span className="na">—</span>}{second ? <div className="line" /> : null}
          {/* 2026-10-29 (owner) · the second extra signature, below the first, in the order it is given */}
          {second ? (n.signers || []).map((sg, i) => (
            <React.Fragment key={i}>{sg.label}: {sg.role || <span className="miss">not stated</span>}<div className="line" /></React.Fragment>
          )) : null}
        </td>
      </tr>
    );
  });
}

export default function RecordView() {
  const { recipe_id: recipeId } = useParams();
  const navigate = useNavigate();
  usePageReturn("/record-designer");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getRecipe(recipeId).then((d) => alive && setState(d)).catch(() => alive && setState(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [recipeId]);

  const recipe = state?.authored?.recipe;
  const spans = state?.authored?.spans || [];
  // The app header names the product and the record from two store fields; this page sets
  // them the way the designer does, so the header does not keep naming the last record opened.
  const dispatch = useDispatch();
  useEffect(() => {
    if (!recipe) return;
    dispatch(addField({ key: "Product", value: recipe.product || "" }));
    dispatch(addField({ key: "MBMR", value: recipe.name || recipe.code || "" }));
  }, [recipe?.product, recipe?.name, recipe?.code, dispatch]);
  const processes = useMemo(() => {
    const by = new Map();
    (recipe?.operations || []).forEach((op) => {
      const k = op.process_id ?? op.process_name ?? "—";
      if (!by.has(k)) by.set(k, { name: op.process_name || "Process", ops: [] });
      by.get(k).ops.push(op);
    });
    return [...by.values()];
  }, [recipe]);

  if (loading) return <Flex justify="center" style={{ padding: 48 }}><Spin /></Flex>;
  if (!recipe) return <Empty description="This record could not be loaded." />;

  const refs = recipe.references || {};
  // The activity-duration table (BMR p. 98): every batch-scoped clock in the record and the
  // step it anchors to. Read off the rules, not typed.
  const clocks = [];
  (recipe.operations || []).forEach((op) => {
    const walkN = (nodes) => (nodes || []).forEach((n) => {
      (n.rules || []).forEach((r) => {
        if (r.rule_type === "CLOCK" && (r.scope === "BATCH" || r.scope === "LOT")) {
          const mins = r.max_duration ?? r.params?.max_duration; const mn = r.min_duration ?? r.params?.min_duration;
          const fmt = (sec) => (sec == null ? null : sec >= 3600 ? `${Math.round(sec / 360) / 10} h` : `${Math.round(sec / 60)} min`);
          const limit = [mn != null ? `NLT ${fmt(mn)}` : null, mins != null ? `NMT ${fmt(mins)}` : null].filter(Boolean).join(" · ") || "—";
          // One row per clock, as the document lists them; a clock anchored on two steps
          // (the CRT exposure runs from stoppering to packing) names both anchors.
          const same = clocks.find((c) => c.name === r.name && c.limit === limit);
          if (same) same.where += ` · ${op.name} · ${n.code}`;
          else clocks.push({ key: `${n.code}-${r.id || r.binding}`, name: r.name, limit, where: `${op.name} · ${n.code}` });
        }
      });
      walkN(n.children);
    });
    walkN(op.children);
  });
  const bom = refs.bom || {};
  const rec = Object.fromEntries((refs.record || []).map((d) => [d.label, d.value]));
  const head = (section) => (
    <div className="bmr-ph">
      <span className="co">{recipe.product || "Support record"}</span>
      <span className="ttl">BMR – {section}</span>
      <span className="kv">Batch No.: <b className="blank">__________</b></span>
      <span className="kv">BMR No.: <b>{recipe.code}</b> rev {recipe.revision}</span>
    </div>
  );
  let section = 0;

  // html2canvas reads only rgb() / hex; the stylesheet's mixed colours compute to `color(srgb …)`.
  // On the copy it draws (never the page itself), each such colour is written back as rgba().
  const COLOUR_PROPS = ["color", "background-color", "border-top-color", "border-right-color", "border-bottom-color",
    "border-left-color", "outline-color", "text-decoration-color", "fill", "stroke", "box-shadow", "background-image"];
  const toRgba = (v) => String(v).replace(/color\(srgb ([\d.e-]+) ([\d.e-]+) ([\d.e-]+)(?: \/ ([\d.e-]+))?\)/g,
    (_, r, g, b, a) => `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a ?? 1})`)
    .replace(/(oklch|oklab|lab|lch)\([^)]*\)/g, "rgba(0, 0, 0, 0)");
  const plainColours = (doc) => {
    const win = doc.defaultView;
    doc.querySelectorAll(".bmr-doc, .bmr-doc *").forEach((el) => {
      const cs = win.getComputedStyle(el);
      COLOUR_PROPS.forEach((prop) => {
        const v = cs.getPropertyValue(prop);
        if (v && /color\(|oklch|oklab|lab\(|lch\(/.test(v)) el.style.setProperty(prop, toRgba(v), "important");
      });
    });
  };

  // 2026-10-14 (owner) · the record as a PDF file: each printed page of the paper view drawn onto A4,
  // a page taller than A4 continued on the next sheet. Nothing leaves the browser.
  const downloadPdf = async () => {
    const pages = Array.from(document.querySelectorAll(".bmr-doc .bmr-page"));
    if (!pages.length) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = pdf.internal.pageSize.getWidth(), H = pdf.internal.pageSize.getHeight(), M = 8;
      const w = W - 2 * M, h = H - 2 * M;
      let first = true;
      for (const page of pages) {
        const canvas = await html2canvas(page, { scale: 1.6, backgroundColor: "#ffffff", useCORS: true, logging: false,
                                                 onclone: (doc) => plainColours(doc) });
        const sliceH = Math.floor(canvas.width * (h / w));          // canvas px that fill one sheet
        for (let y = 0; y < canvas.height; y += sliceH) {
          const part = document.createElement("canvas");
          part.width = canvas.width; part.height = Math.min(sliceH, canvas.height - y);
          part.getContext("2d").drawImage(canvas, 0, y, canvas.width, part.height, 0, 0, canvas.width, part.height);
          if (!first) pdf.addPage();
          first = false;
          pdf.addImage(part.toDataURL("image/jpeg", 0.8), "JPEG", M, M, w, (part.height * w) / canvas.width);
        }
      }
      pdf.save(`${String(recipe.code || recipe.name || "record").replace(/[^\w.-]+/g, "_")}_rev${recipe.revision ?? ""}.pdf`);
    } catch (e) {
      console.error("Download PDF failed", e);
      message.error("The PDF could not be made — use Print record and save as PDF instead.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="dz bmr">
      <Flex justify="space-between" align="center" wrap gap={12} className="bmr-bar">
        <Space size={10}>
          <Text type="secondary" style={{ fontSize: 12 }}>Read-only · shaped as the batch record it becomes · nothing here can be changed</Text>
        </Space>
        <Space size={8}>
          <Button size="small" icon={<Download size={13} />} loading={pdfBusy} onClick={downloadPdf}>Download PDF</Button>
          <Button size="small" type="primary" icon={<PencilRuler size={13} />} onClick={() => navigate(`/record-designer/${recipeId}`)}>Open in designer</Button>
        </Space>
      </Flex>
      <PageRail items={[{ key: "print", icon: Printer, label: "Print record", onClick: () => window.print() }]} />

      <article className="bmr-doc">
        {/* ---- front matter: product information ------------------------------------ */}
        <section className="bmr-page">
          {head("Product information")}
          <h1>{recipe.name || recipe.code}</h1>
          <table className="bmr-kv">
            <tbody>
              {[["Product", recipe.product], ["Master record", `${recipe.code} · rev ${recipe.revision} · ${recipe.state}`],
                ["Ref. MFR", rec["MFR version"] ? `version ${rec["MFR version"]}` : null], ["Batch size", rec["Batch size"] || bom.summary?.find((d) => d.label === "Batch size")?.value],
                ["Theoretical output", bom.summary?.find((d) => d.label === "Theoretical output")?.value],
                ["Change control", rec["Change control"]], ["Change reason", rec["Change reason"]], ["Valid", rec["Valid from"] ? `${rec["Valid from"]} → ${rec["Valid to"] || "open"}` : null],
                ["Market", rec["Market"]], ["Stage", rec["Stage"]]]
                .filter(([, v]) => v).map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}
            </tbody>
          </table>

          <h2>Bill of materials</h2>
          {bom.lines?.length ? (
            <table className="bmr-tab">
              <thead><tr><th>Sr. No.</th><th>Name of material</th><th>Pharmacopoeial / role</th><th>SAP code</th><th>Quantity per batch</th><th>To dispense</th><th>Overage</th><th>Tolerance</th><th>Charged at</th></tr></thead>
              <tbody>{bom.lines.map((l, i) => (
                <tr key={i}><td className="no">{l.sequence ?? i + 1}</td><td>{l.material}</td><td>{l.role}</td><td className="code">{l.sap_code}</td>
                  <td className="num">{l.qs ? "q.s." : l.quantity}</td><td className="num">{l.dispense}</td><td className="num">{l.overage}</td><td className="num">{l.tolerance}</td><td>{l.stage}</td></tr>
              ))}</tbody>
            </table>
          ) : <p className="muted">{bom.note || "No current MBOM on this record."}</p>}

          <h2>Index</h2>
          <table className="bmr-tab idx">
            <thead><tr><th>Sr. No.</th><th>Section</th><th>Operations</th></tr></thead>
            <tbody>{processes.map((p, i) => (
              <tr key={i}><td className="no">{i + 1}</td><td>{p.name}</td><td className="num">{p.ops.length}</td></tr>
            ))}{spans.length ? <tr><td className="no">{processes.length + 1}</td><td>Continuous operations</td><td className="num">{spans.length}</td></tr> : null}</tbody>
          </table>

          {refs.abbreviations?.length ? (<>
            <h2>Abbreviations</h2>
            <table className="bmr-tab abbr"><tbody>{refs.abbreviations.map((a) => <tr key={a.term}><th>{a.term}</th><td>:</td><td>{a.meaning}</td></tr>)}</tbody></table>
          </>) : null}
          {(refs.quality?.cqas || []).length ? (
            <>
              <h2>What controls quality</h2>
              <table className="bmr-tab">
                <thead><tr><th>Quality attribute</th><th>Specification</th><th>Controlled by (CPP steps)</th><th>Material attributes (CMA)</th></tr></thead>
                <tbody>
                  {refs.quality.cqas.map((q) => (
                    <tr key={q.code} className={(q.controlled_by || []).length ? "" : "bad"}>
                      <td><b>{q.name}</b><div className="i muted">{q.code}</div></td>
                      <td>{q.specification}<div className="i muted">{q.source_ref}</div></td>
                      <td className="code">{(q.controlled_by || []).map((s) => s.code).join(" · ") || <span className="miss">nothing in this record controls it</span>}</td>
                      <td>{(q.cmas || []).map((m, i) => <div key={i}>{m.material} — {m.attribute}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
          <h2>Process flow</h2>
          <ol className="bmr-flow">
            {processes.map((p, pi) => (
              <li key={pi}><b>{p.name}</b>
                {[...new Set(p.ops.map((o) => o.grade).filter(Boolean))].length
                  ? <span className="muted"> · {[...new Set(p.ops.map((o) => o.grade).filter(Boolean))].map((g) => g.replace("GRADE_", "Grade ")).join(" / ")}{p.ops.some((o) => o.aseptic) ? " · aseptic boundary" : ""}</span> : null}
                <span className="muted"> — {p.ops.map((o) => o.name).join(" · ")}</span></li>
            ))}
          </ol>
          <h2>BMR review details</h2>
          <table className="bmr-tab signoff">
            <thead><tr><th /><th>Prepared by</th><th>Reviewed by</th><th>Approved by</th></tr></thead>
            <tbody>
              <tr><th>Name</th><td className="blank" /><td className="blank" /><td className="blank" /></tr>
              <tr><th>Department</th><td>Production</td><td>Production · QA</td><td>Head QA</td></tr>
              <tr><th>Signature & date</th><td className="blank" /><td className="blank" /><td className="blank" /></tr>
            </tbody>
          </table>
          <h2>Table of content</h2>
          <table className="bmr-tab index">
            <thead><tr><th>Sr. No.</th><th>Section</th></tr></thead>
            <tbody>
              {["General instructions", "Abbreviations", "List of equipment", "Specimen log", "Continuous operations",
                ...processes.map((p) => p.name), "Activity duration", "List of attachments", "Revision history"].map((t, i) => (
                <tr key={t}><td className="no">{i + 1}</td><td>{t}</td></tr>
              ))}
            </tbody>
          </table>
          {refs.instructions ? (<><h2>General instructions</h2><p className="gi">{refs.instructions}</p></>) : null}
          {(refs.equipment || []).length ? (
            <>
              <h2>List of equipment</h2>
              <table className="bmr-tab">
                <thead><tr><th>Sr. No.</th><th>Unit operation</th><th>Name of equipment</th><th>Equipment ID</th></tr></thead>
                <tbody>
                  {refs.equipment.map((m, i) => (
                    <tr key={m.code || m.name}><td className="no">{i + 1}</td><td>{(m.operations || []).join(" · ")}</td><td>{m.name}</td><td className="code">{m.code}</td></tr>
                  ))}
                </tbody>
              </table>
              <p className="muted">Ensure the performance qualification / verification / calibration due date (as applicable) for all equipment.</p>
            </>
          ) : null}
          {refs.acknowledgement ? (
            <>
              <h2>Specimen log — acknowledgement</h2>
              <p className="gi">Individuals involved in the execution of this record fill this log. Signing below
                acknowledges they have read and understood the general instructions and abbreviations
                (block v{refs.acknowledgement.version} · {String(refs.acknowledgement.hash || "").slice(0, 12)})
                and their responsibilities in this record.{refs.acknowledgement.required ? " Required before a person's first entry." : ""}</p>
              <table className="bmr-tab">
                <thead><tr><th>Emp. No.</th><th>Employee name</th><th>Department</th><th>Specimen signature</th><th>Date</th></tr></thead>
                <tbody>
                  {Array.from({ length: refs.acknowledgement.specimen_rows || 0 }).map((_, i) => (
                    <tr key={i}><td className="blank" /><td className="blank" /><td className="blank" /><td className="blank" /><td className="blank" /></tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </section>

        {/* ---- continuous operations, before the sections they run across ------------- */}
        {spans.length ? (
          <section className="bmr-page">
            {head("Continuous operations")}
            <h1>Continuous operations</h1>
            {spans.map((sp) => (
              <div key={sp.id} className="bmr-span">
                <h2>{sp.name} <span className="code">{sp.id}</span></h2>
                <p className="muted">
                  {sp.span_kind} · from <b>{sp.from?.operation}</b> ({sp.from?.at}) to <b>{sp.to?.operation}</b> ({sp.to?.at}) ·
                  covers {sp.covers?.length || 0} operations{sp.cadence ? ` · ${sp.cadence}` : ""}{sp.role ? ` · ${sp.role}` : ""}
                  {sp.on_breach?.effect ? ` · on breach ${sp.on_breach.effect}${sp.on_breach.scope ? ` (${sp.on_breach.scope})` : ""}` : ""}
                </p>
                {(sp.children || []).length ? (
                  <table className="bmr-tab steps">
                    <thead><tr><th>Sr. No.</th><th>Details</th><th>Limit</th><th>Observation</th><th>Done By<br /><small>(Sign & Date)</small></th><th>Verified By<br /><small>(Sign & Date)</small></th></tr></thead>
                    <tbody><StepRows nodes={sp.children} start={{ n: 0 }} recipeId={recipeId} /></tbody>
                  </table>
                ) : null}
              </div>
            ))}
          </section>
        ) : null}

        {/* ---- one section per process; one table per operation ----------------------- */}
        {processes.map((p, pi) => (
          <section key={pi} className="bmr-page">
            {head(p.name)}
            <h1><span className="sn">{pi + 1}</span>{p.name}</h1>
            {p.ops.map((op, oi) => {
              section += 1;
              const covering = spans.filter((sp) => (op.covered_by || []).includes(sp.id));
              return (
                <div key={op.id} className="bmr-op">
                  <h2><span className="sn">{pi + 1}.{oi + 1}</span>{op.name}<span className="code">{op.code}</span>{op.phase ? <span className="phase">{op.phase}</span> : null}</h2>
                  {covering.length ? (
                    <p className="muted cov">Runs under {covering.map((sp) => sp.name).join(", ")} — recorded against this operation as well.</p>
                  ) : null}
                  {op.grade ? (
                    <p className="muted cov">Area: {op.grade.replace("GRADE_", "Grade ").replace("_", " ")}{op.aseptic ? " — inside the aseptic boundary; environmental monitoring runs throughout" : ""}</p>
                  ) : null}
                  {op.handover?.required ? (
                    <p className="muted cov"><b>Handover acknowledgement</b> — before a person's first action on this operation they read and sign: {op.handover.text}</p>
                  ) : null}
                  {op.concurrency?.stations >= 2 ? (
                    <p className="muted cov">Up to {op.concurrency.stations} stations at once — each iteration is one person's; record who, per iteration.</p>
                  ) : null}
                  {(op.required_attachments || []).length ? (
                    <p className="muted cov">Attach: {op.required_attachments.map((a) => a.label).join(" · ")}</p>
                  ) : null}
                  {op.inspection?.defect_set ? (
                    <>
                      <h3>Type of defects — {op.inspection.defect_set.name}</h3>
                      <table className="bmr-tab defects">
                        <thead><tr><th>Category</th><th>Type of defect</th><th>Code</th><th>Criticality</th></tr></thead>
                        <tbody>{op.inspection.defect_set.codes.map((c) => (
                          <tr key={c.code}><td>{c.category}</td><td>{c.name}</td><td className="code">{c.code}</td><td>{c.criticality}</td></tr>
                        ))}</tbody>
                      </table>
                    </>
                  ) : null}
                  {op.inspection?.ipc_sampling ? (
                    <>
                      <h3>In-process check — sampling plan</h3>
                      <p className="muted">{op.inspection.ipc_sampling.plan}. In-process after NLT {op.inspection.ipc_sampling.accumulate_min_units} and NMT {op.inspection.ipc_sampling.accumulate_max_units} accumulated units.</p>
                      {/* The plan is the operation's raw config, not a checked shape: a plan saved
                          without bands must read as "no table", not take the whole record down. */}
                      {op.inspection.ipc_sampling.bands?.length ? (
                        <>
                          <table className="bmr-tab plan">
                            <thead><tr><th>Lot size</th><th>Sample size</th>{(op.inspection.ipc_sampling.bands[0].levels || []).map((l) => <th key={l.level}>Level {l.level}<br /><small>Ac / Re</small></th>)}</tr></thead>
                            <tbody>{op.inspection.ipc_sampling.bands.map((b) => (
                              <tr key={b.lot_min}><td>{b.lot_min} to {b.lot_max}</td><td>{b.sample}</td>{(b.levels || []).map((l) => <td key={l.level}>{l.accept == null ? "#" : l.accept} / {l.reject}</td>)}</tr>
                            ))}</tbody>
                          </table>
                          <p className="muted"># acceptance not permitted at this sample size.</p>
                        </>
                      ) : <p className="muted">No sampling bands on this plan.</p>}
                    </>
                  ) : null}
                  {op.inspection?.aql ? (
                    <p className="muted cov">AQL: {op.inspection.aql.plan} · level {op.inspection.aql.level} · critical {op.inspection.aql.critical} · major {op.inspection.aql.major} · minor {op.inspection.aql.minor}.</p>
                  ) : null}
                  {op.children?.length ? (
                    <table className="bmr-tab steps">
                      <thead><tr><th>Sr. No.</th><th>Details</th><th>Limit</th><th>Observation</th><th>Done By<br /><small>(Sign & Date)</small></th><th>Verified By<br /><small>(Sign & Date)</small></th></tr></thead>
                      <tbody><StepRows nodes={op.children} start={{ n: 0 }} recipeId={recipeId} /></tbody>
                    </table>
                  ) : <p className="muted">No steps in this operation.</p>}
                  <table className="bmr-tab times">
                    <tbody><tr><th>Start time</th><td className="blank">_ _ : _ _</td><th>End time</th><td className="blank">_ _ : _ _</td><th>Done by</th><td className="blank" /></tr></tbody>
                  </table>
                </div>
              );
            })}
          </section>
        ))}

        {/* ---- back matter: activity durations, the list of attachments, revision history ----- */}
        <section className="bmr-page">
          {head("Activity duration · attachments · revision history")}
          <h1>Activity duration</h1>
          <table className="bmr-tab">
            <thead><tr><th>Sr. No.</th><th>Activity duration</th><th>Limit</th><th>Observation</th><th>Recorded by</th></tr></thead>
            <tbody>
              {clocks.map((c, i) => (
                <tr key={c.key}><td className="no">{i + 1}</td><td>{c.name}<div className="i muted">{c.where}</div></td><td className="lim">{c.limit}</td><td className="blank">________</td><td className="blank" /></tr>
              ))}
            </tbody>
          </table>
          {(refs.attachments || []).length ? (
            <>
              <h1>List of attachments</h1>
              <table className="bmr-tab">
                <thead><tr><th>Sr. No.</th><th>Attachment</th><th>With operation</th><th>Attached (✓)</th></tr></thead>
                <tbody>
                  {refs.attachments.map((a, i) => (
                    <tr key={a.code}><td className="no">{i + 1}</td><td>{a.label}</td><td>{a.operation_name}</td><td className="blank" /></tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
          <h1>Revision history</h1>
          <table className="bmr-tab">
            <thead><tr><th>Revision</th><th>Details</th><th>Change control</th><th>Effective</th></tr></thead>
            <tbody>
              <tr><td>{recipe.revision}</td><td>{recipe.state} · {rec["Change reason"] || "current revision of the master record"}</td><td>{rec["Change control"] || rec["Change control ID"] || "—"}</td><td className="blank" /></tr>
            </tbody>
          </table>
          <p className="muted">{section} operation sections · {processes.length} processes · {spans.length} continuous operation{spans.length === 1 ? "" : "s"}.</p>
        </section>
      </article>
    </div>
  );
}
